import React, { useState, useCallback, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Input } from '../Input/Input';
import { Select } from '../Select/Select';
import { LookupField } from '../LookupField';
import { Switch } from '../Switch/Switch';
import { Button } from '../Button/Button';
import { AlertDialog } from '../AlertDialog/AlertDialog';
import { Dropdown } from '../Dropdown/Dropdown';
import { CompanySelector } from './CompanySelector';
import type { MasterCrudFormProps, MasterCrudField, FormState } from './MasterCrud.types';
import { CloseIcon as XMarkIcon, RevertIcon, GlobeAltIcon, Bars3Icon, PlusIcon, LinkIcon } from './icons';

// Entidades Dinámicas
import { DynamicEntities, type DynamicEntitiesRef } from '../DynamicEntities/ui/DynamicEntities';
import { MasterCrudDynamicEntities } from './MasterCrudDynamicEntities';
import { DynamicEntitiesRegistrationContext } from './DynamicEntitiesRegistrationContext';
import { useOptionalDynamicEntitiesContext } from '../DynamicEntities/core/DynamicEntitiesProvider';
import { FeatureService, type Feature } from '../DynamicEntities/admin/services/FeatureService';

// Gestión de Archivos
import { MasterCrudFileManager } from './MasterCrudFileManager';
import { FileGalleryRegistrationContext } from './FileGalleryRegistrationContext';

/**
 * Componente interno para resolver nombres de Lookups en Valores Globales.
 *
 * Construye un SearchRequest completo (con `fields` y el filtro usando `valueField`)
 * para que backends reales que requieren el contrato estricto de LookupField funcionen
 * correctamente y muestren el display text en lugar del GUID/ID crudo.
 */
const GlobalLookupResolver = ({
  id,
  lookupConfig,
  t
}: {
  id: any,
  lookupConfig: any,
  t: (key: string, def?: string) => string
}) => {
  const [resolvedValue, setResolvedValue] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (id === undefined || id === null || id === '') return;

    const fetchResolvedValue = async () => {
      try {
        // El campo que actúa como PK en la respuesta del fetcher.
        // LookupField usa 'Id' (PascalCase) como estándar, pero lookupConfig.valueField
        // puede declarar un nombre diferente (ej: 'id', 'uuid', 'codigo').
        const valueField: string = lookupConfig.valueField || 'Id';

        // Construir el array de fields para que el backend sepa qué columnas retornar.
        // Sin este campo el contrato SearchRequest queda incompleto y backends reales
        // retornan data vacía o sin las columnas de displayFields → mostraba GUID.
        const requestFields: string[] = [
          ...new Set([...lookupConfig.displayFields, valueField])
        ];

        // Usar valueField como clave del filtro en lugar de 'Id' hardcodeado,
        // para que el match funcione independientemente de cómo se llame el PK.
        const filterCondition: Record<string, any> = { [valueField]: id };

        const response = await lookupConfig.fetcher(lookupConfig.entity, {
          fields: requestFields,
          filters: [[filterCondition]],
          page: 1,
          pageSize: 1
        });

        if (response.data && response.data[0]) {
          const record = response.data[0];
          const text = lookupConfig.displayFields
            .map((f: string) => record[f])
            .filter(Boolean)
            .join(' - ');
          setResolvedValue(text || String(id));
        } else {
          setResolvedValue(String(id));
        }
      } catch (e) {
        setResolvedValue(String(id));
      } finally {
        setLoading(false);
      }
    };

    fetchResolvedValue();
  }, [id, lookupConfig]);

  if (loading) return <span className="animate-pulse opacity-70 italic text-[11px]">{t('loading', 'Cargando...')}</span>;
  return <span>{resolvedValue}</span>;
};

const defaultInitialData = {};

/**
 * MasterCrudForm - Generador dinámico de formularios para MasterCrud
 *
 * Genera automáticamente un formulario basado en la definición de campos,
 * reutilizando los componentes atómicos del sistema de diseño.
 */
export const MasterCrudForm = forwardRef(<T extends Record<string, any>>(
  {
    fields,
    entityName,
    dynamicFeatureCode,
    initialData,
    isEditing = false,
    loading = false,
    onSubmit,
    onCancel,
    saveButtonText,
    cancelButtonText,
    columns = 2,
    width = '600px',
    className = '',
    actions = [],
    hideHeaderFooter = false,
    fullWidthButton = false,
    isReadOnly = false,
    idField = 'id',
    id,
    selectedCompany,
    activeByCompany = false,
    companyRequired = false,
    showGlobalValues = false,
    onSearchMatches,
    canOverrideMultiCompany = true,
    companies = [],
    onCompanyChange,
    onShowGlobalValuesChange,
    onUseGlobalData,
    onCreateNew,
    onLinkCompanies,
    renderContent,
    hideDynamicTabs = false,
    registerDynamicRef: propRegisterDynamicRef,
    t,
    showValidationErrorDialog = false, // Restaurado a false por defecto
    formTabs,
    featCodeFileManager,
    fileManagerConfig,
  }: MasterCrudFormProps<T>,
  ref: React.Ref<any>
) => {
  const effectiveHideDynamicTabs = hideDynamicTabs || !!renderContent;

  // ===== PESTAÑAS DE CAMPOS (formTabs) — renderizado automático en tabs (opcional) =====
  // Solo aplica cuando se usa el render automático (sin renderContent) y hay >1 pestaña.
  const hasFieldTabs = !renderContent && Array.isArray(formTabs) && formTabs.length > 1;
  const fieldTabs = hasFieldTabs ? formTabs! : [];
  // Devuelve la clave de pestaña de un campo: config.tab, o la pestaña que lo lista en `fields`,
  // o la primera pestaña como fallback.
  const getFieldTabKey = React.useCallback((accessorKey: string): string => {
    if (!hasFieldTabs) return 'main';
    const byList = fieldTabs.find(ft => ft.fields?.includes(accessorKey));
    if (byList) return byList.key;
    const field = fields.find(f => (f.accessorKey as string) === accessorKey);
    const cfgTab = field?.config?.tab;
    if (cfgTab && fieldTabs.some(ft => ft.key === cfgTab)) return cfgTab;
    return fieldTabs[0].key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFieldTabs, fieldTabs, fields]);

  const defaultId = React.useId();
  const formId = id || `form-${defaultId}`;

  // Use translations for defaults if props are not provided
  const finalSaveText = saveButtonText || t('save');
  const finalCancelText = cancelButtonText || t('cancel');
  // ===== ESTADO DEL FORMULARIO =====

  const [formState, setFormState] = useState<FormState<T>>({
    values: (initialData || defaultInitialData) as Partial<T>,
    errors: {},
    touched: {},
    isSubmitting: false,
  });

  // Referencia mutable para sincronizar siempre los valores más recientes
  // Esto previene cierres obsoletos (stale closures) en handleSubmit y callbacks externos
  const valuesRef = useRef<Partial<T>>((initialData || defaultInitialData) as Partial<T>);

  useEffect(() => {
    valuesRef.current = formState.values;
  }, [formState.values]);

  const [errorAlert, setErrorAlert] = useState<{ show: boolean; message: string; title?: string }>({
    show: false,
    message: '',
  });

  const [devDynamicEntitiesWarning, setDevDynamicEntitiesWarning] = useState(false);

  const lastSearchedValue = useRef<string>('');
  // ref to the actual <form> DOM element, used by the imperative submit() to dispatch a native event
  const formElementRef = useRef<HTMLFormElement | null>(null);

  // Ref de seguridad para evitar doble envío incluso en micro-tareas
  const isSubmittingRef = useRef(false);

  // Variable para rastrear el ID del registro inicializado y evitar resets accidentales
  const initializedRecordIdRef = useRef<string | number | null>(null);

  // ===== SINCRONIZAR DATOS INICIALES =====
  useEffect(() => {
    const currentId = (initialData as any)?.[idField];
    
    // Solo reseteamos el estado si el ID ha cambiado o si no se ha inicializado nada aún
    if (initializedRecordIdRef.current !== currentId || (!currentId && !initializedRecordIdRef.current)) {
      setFormState((prev) => ({
        ...prev,
        values: (initialData || defaultInitialData) as Partial<T>,
        errors: {},
        touched: {},
      }));
      initializedRecordIdRef.current = currentId || null;
      isSubmittingRef.current = false;
    }
  }, [initialData, idField]);

  // ===== LÓGICA DE ENTIDADES DINÁMICAS =====
  const dynamicContext = useOptionalDynamicEntitiesContext();
  const [dynamicFeature, setDynamicFeature] = useState<Feature | null>(null);
  const [activeGlobalTab, setActiveGlobalTab] = useState<string>(() => (hasFieldTabs ? fieldTabs[0].key : 'main'));
  const dynamicRefs = useRef<Record<string, DynamicEntitiesRef | null>>({});

  // TabBar: indicador deslizante con rebote (S2.3). El rebote lo da el easing de `left`;
  // el ancho cambia suave sin overshoot. Grosor constante 2.5px.
  const tabsRowRef = useRef<HTMLDivElement>(null);
  const tabIndicatorRef = useRef<HTMLSpanElement>(null);
  const moveTabIndicator = useCallback((instant?: boolean) => {
    const row = tabsRowRef.current;
    const ind = tabIndicatorRef.current;
    if (!row || !ind) return;
    const active = row.querySelector<HTMLElement>(`[data-tabkey="${activeGlobalTab}"]`);
    if (!active) return;
    if (instant) ind.style.transition = 'none';
    ind.style.left = `${active.offsetLeft}px`;
    ind.style.width = `${active.offsetWidth}px`;
    if (instant) { void ind.offsetWidth; ind.style.transition = ''; }
  }, [activeGlobalTab]);
  // Cambio de pestaña: reposiciona con animación (rebote).
  useEffect(() => { moveTabIndicator(); }, [moveTabIndicator]);
  // Cambio de errores de validación: al aparecer/desaparecer los badges rojos, el ancho de las
  // pestañas cambia; se re-mide **con animación** para que el subrayado se **deslice** suavemente
  // hasta quedar bajo la pestaña activa (no "entre dos" pestañas), conservando el efecto de
  // movimiento. (No dependemos de moveTabIndicator para no re-disparar en cada cambio de pestaña.)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { moveTabIndicator(); }, [formState.errors]);

  // Al activar las pestañas de campos (formTabs), si la pestaña activa no es una de ellas
  // (p. ej. venía en 'main' al alternar "Con pestañas"), seleccionar la primera pestaña.
  useEffect(() => {
    if (hasFieldTabs && !fieldTabs.some((ft) => ft.key === activeGlobalTab)) {
      setActiveGlobalTab(fieldTabs[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFieldTabs, activeGlobalTab]);

  // ===== DETECCIÓN EN DEV: renderForm sin MasterCrudDynamicEntities =====
  const dynamicEntitiesRegisteredRef = useRef(false);
  const registerDynamicEntitiesPresence = useCallback(() => {
    dynamicEntitiesRegisteredRef.current = true;
  }, []);
  const registrationContextValue = React.useMemo(
    () => ({ register: registerDynamicEntitiesPresence }),
    [registerDynamicEntitiesPresence]
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!renderContent || !dynamicFeatureCode) return;
    if (loading) return; // Mientras loading=true el renderForm puede mostrar skeleton sin MasterCrudDynamicEntities
    if (!dynamicEntitiesRegisteredRef.current) {
      setDevDynamicEntitiesWarning(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // Re-evalúa cuando el formulario termina de cargar

  useEffect(() => {
    if (dynamicFeatureCode && dynamicContext?.baseUrl) {
      const service = new FeatureService(dynamicContext.baseUrl, dynamicContext.fetcher);
      service.getByPath(dynamicFeatureCode)
        .then(f => {
          setDynamicFeature(f);
        })
        .catch(err => console.warn("[MasterCrudForm] Feature not found or dynamic entities disabled for this feature", err));
    }
  }, [dynamicFeatureCode, dynamicContext?.baseUrl, dynamicContext?.fetcher]);

  const hasStandardDynamicEntities = dynamicFeature?.featureEntities?.some((fe: any) => !fe.dynamicEntity?.isPlansAndCriteria);
  const hasPlansAndCriteriaEntities = dynamicFeature?.featureEntities?.some((fe: any) => !!fe.dynamicEntity?.isPlansAndCriteria);

  // ===== LÓGICA DE GESTIÓN DE ARCHIVOS =====
  const fileGallerySaveRef = useRef<((recordId: string | number) => Promise<boolean>) | null>(null);
  const fileGalleryRegisteredRef = useRef(false);
  const [devFileManagerWarning, setDevFileManagerWarning] = useState(false);

  const registerFileGallerySave = useCallback((saveCallback: (recordId: string | number) => Promise<boolean>) => {
    fileGallerySaveRef.current = saveCallback;
    fileGalleryRegisteredRef.current = true;
  }, []);

  const fileGalleryRegistrationContextValue = React.useMemo(
    () => ({ register: registerFileGallerySave }),
    [registerFileGallerySave]
  );

  // Detección en DEV: renderForm sin MasterCrudFileManager cuando se requiere.
  // Solo aplica si el Feature del backend tiene has_file_management=true; si está
  // desactivado en BD el tab no se renderiza, así que no exigimos el componente.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!renderContent || !featCodeFileManager) return;
    if (!dynamicFeature?.hasFileManagement) return;
    if (loading) return;
    if (!fileGalleryRegisteredRef.current) {
      setDevFileManagerWarning(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, dynamicFeature]);

  // El tab de gestión de archivos requiere TRES condiciones:
  //   1. Wiring del desarrollador: `featCodeFileManager` + `fileManagerConfig`
  //      (o un service con `getFiles/uploadFile/deleteFile`, que MasterCrud
  //       construye en `effectiveFileManagerConfig`).
  //   2. Flag del backend: el Feature asociado (`dynamicFeatureCode`) debe tener
  //      `has_file_management = true` en la columna del proyecto de entidades
  //      dinámicas. Es la fuente de verdad final — desactivarla en BD oculta el
  //      tab aun si el desarrollador implementó todas las props.
  //   3. Registro persistido: en CREATE el registro aún no existe, así que no
  //      hay entityId para asociar archivos. El tab solo se muestra en EDIT.
  const fileManagerRecordId = (initialData as any)?.[idField];
  const hasFileManagement =
    !!dynamicFeature?.hasFileManagement &&
    !!featCodeFileManager &&
    !!fileManagerConfig &&
    !!fileManagerRecordId;

  // ===== EXPONER MÉTODOS AL PADRE =====
  useImperativeHandle(ref, () => ({
    revertAllToGlobal: () => {
      const globalData = (initialData as any)?._globalValues;
      if (!globalData) return;

      const overrideableFields = fields.filter(f => f.config?.allowMultiCompanyOverride);

      setFormState(prev => {
        const newValues = { ...prev.values } as any;
        overrideableFields.forEach(field => {
          const accessor = field.accessorKey as string;
          if (globalData[accessor] !== undefined) {
            newValues[accessor] = globalData[accessor];
          }
        });
        return { ...prev, values: newValues };
      });
    },
    saveDynamicEntities: async (recordId: string | number) => {
      const entityCodes = Object.keys(dynamicRefs.current);
      const activeEntities = entityCodes.filter(code => !!dynamicRefs.current[code]);

      if (activeEntities.length > 0) {
        const savePromises = activeEntities.map(async (code) => {
          const entityRef = dynamicRefs.current[code];
          if (entityRef && typeof entityRef.save === 'function') {
            return await entityRef.save(String(recordId));
          }
          return true;
        });
        const results = await Promise.all(savePromises);
        return !results.some(res => res === false); // Devuelve true si no hay fallos
      }
      return true;
    },
    // Guardar gestión de archivos tras el guardado del registro base
    saveFileManagement: async (recordId: string | number): Promise<boolean> => {
      if (!hasFileManagement || !fileGallerySaveRef.current) return true;
      return fileGallerySaveRef.current(recordId);
    },
    // Método para disparar el submit desde fuera
    submit: () => {
      console.log("%c[MasterCrudForm] submit() invocado desde ref externa", "color: #0ea5e9; font-weight: bold; padding: 2px; border: 1px solid #0ea5e9; border-radius: 4px;");
      
      if (formElementRef.current) {
        // Disparamos el evento nativo para que cualquier interceptor (como ItemMasterForm) actúe.
        const event = new Event('submit', { bubbles: true, cancelable: true });
        formElementRef.current.dispatchEvent(event);
      }
      
      // Ejecutamos SIEMPRE handleSubmit.
      // Al usar isSubmittingRef, garantizamos que no haya doble ejecución si React ya capturó el evento.
      // Esto soluciona el bug donde el guardado se detenía si alguien llamaba a e.preventDefault().
      handleSubmit();
    },
    // Cambios sin guardar (dirty state, S2.8): compara los valores actuales contra el snapshot inicial.
    getChanges: (): { label: string; from: any; to: any }[] => {
      const initial = (initialData || defaultInitialData) as any;
      const norm = (v: any) => (v === undefined || v === null ? '' : v);
      const changes: { label: string; from: any; to: any }[] = [];
      fields.forEach((f) => {
        const k = f.accessorKey as string;
        const from = initial?.[k];
        const to = (formState.values as any)?.[k];
        if (String(norm(from)) !== String(norm(to))) {
          changes.push({ label: typeof f.header === 'string' ? f.header : k, from: norm(from), to: norm(to) });
        }
      });
      return changes;
    },
    isDirty: (): boolean => {
      const initial = (initialData || defaultInitialData) as any;
      const norm = (v: any) => (v === undefined || v === null ? '' : v);
      return fields.some((f) => {
        const k = f.accessorKey as string;
        return String(norm(initial?.[k])) !== String(norm((formState.values as any)?.[k]));
      });
    },
  }));

  // ===== LÓGICA DE REGISTRO DE REFS DINÁMICOS =====
  const registerDynamicRef = useCallback((entityCode: string, r: any) => {
    if (entityCode) {
      if (r) {
        dynamicRefs.current[entityCode] = r;
      }

      if (propRegisterDynamicRef) {
        propRegisterDynamicRef(entityCode, r);
      }
    }
  }, [propRegisterDynamicRef]);

  // ===== OBTENER VALOR DE UN CAMPO =====
  const getValue = useCallback(
    (accessorKey: string): any => {
      const keys = accessorKey.split('.');
      let value: any = formState.values;
      for (const key of keys) {
        if (value === undefined || value === null) return undefined;
        value = value[key];
      }
      return value;
    },
    [formState.values]
  );

  // ===== ESTABLECER VALOR DE UN CAMPO =====
  const setValue = useCallback((accessorKey: string, value: any) => {
    setFormState((prev) => {
      const keys = accessorKey.split('.');
      const newValues = { ...prev.values } as any;

      if (keys.length === 1) {
        newValues[accessorKey] = value;
      } else {
        let current = newValues;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
      }

      return {
        ...prev,
        values: newValues,
        touched: { ...prev.touched, [accessorKey]: true },
      };
    });
  }, []);

  // ===== MANEJAR REVERSIÓN INDIVIDUAL =====
  const handleRevertSingleField = useCallback((field: MasterCrudField<T>) => {
    const accessor = field.accessorKey as string;
    const globalData = (initialData as any)?._globalValues;

    if (globalData && globalData[accessor] !== undefined) {
      setValue(accessor, globalData[accessor]);
    }
  }, [initialData, setValue]);

  // ===== CONSTRUIR ITEMS DEL DROPDOWN DE ACCIONES PERSONALIZADAS =====
  const actionItems = React.useMemo(() => {
    if (!actions.length) return [];

    // Descartar la opción de Editar si ya estamos en modo Edición
    const filteredActions = isEditing
      ? actions.filter(action => action.id !== 'edit')
      : actions;

    const record = initialData as T;

    return filteredActions.map(action => {
      const isDisabled = typeof action.disabled === 'function' ? action.disabled(record) : action.disabled;
      const actionName = typeof action.name === 'function' ? action.name(record) : action.name;

      return {
        children: actionName,
        icon: typeof action.icon === 'function' ? action.icon(record) : action.icon,
        disabled: isDisabled,
        onClick: () => action.action?.(record),
        className: actionName.toLowerCase().includes('eliminar') || actionName.toLowerCase().includes('delete')
          ? 'text-error-content hover:text-error-content dark:text-error-content'
          : '',
      };
    });
  }, [actions, initialData, isEditing]);

  // ===== CONSTRUIR ITEMS DEL DROPDOWN DE CABECERA =====
  const headerDropdownItems = React.useMemo(() => {
    const items: any[] = [...actionItems];
    const isGlobal = !selectedCompany || String(selectedCompany.uuid) === '000';

    if (activeByCompany && isEditing) {
      const mcActions: any[] = [];

      // 1. Acciones de sucursal (No Global)
      if (!isGlobal) {
        mcActions.push({
          children: t('useGlobalData', 'Usar datos globales'),
          icon: <GlobeAltIcon className="w-4 h-4" />,
          onClick: onUseGlobalData
        });
        mcActions.push({
          children: (
            <div className="flex items-center justify-between w-full gap-4 py-1">
              <span className="text-sm font-medium">{t('showGlobalValues', 'Mostrar valores globales')}</span>
              <Switch
                checked={showGlobalValues}
                onChange={() => { }}
                className="!w-auto pointer-events-none"
              />
            </div >
          ),
          onClick: () => onShowGlobalValuesChange?.(!showGlobalValues),
          className: 'hover:bg-transparent',
        });
        mcActions.push({ isDivider: true });
      }

      // 2. Habilitar (Disponible en Global y Sucursal)
      mcActions.push({
        children: t('linkCompanies', 'Habilitar compañías'),
        icon: <LinkIcon className="w-4 h-4" />,
        onClick: onLinkCompanies
      });

      // 3. Crear nuevo (Siempre en edición)
      mcActions.push({
        children: t('createNew', 'Crear nuevo registro'),
        icon: <PlusIcon className="w-4 h-4" />,
        onClick: onCreateNew
      });

      return [...mcActions, ...items];
    }

    return items;
  }, [actionItems, activeByCompany, isEditing, selectedCompany, onUseGlobalData, showGlobalValues, onShowGlobalValuesChange, onLinkCompanies, onCreateNew]);

  // ===== RESOLVER PROPIEDAD DINÁMICA =====
  const resolveProp = useCallback((prop: boolean | ((data: any) => boolean) | undefined, data: any): boolean => {
    if (typeof prop === 'function') {
      return prop(data);
    }
    return !!prop;
  }, []);

  // ===== VALIDAR CAMPO =====
  const validateField = useCallback(
    (field: MasterCrudField<T>, value: any): string | undefined => {
      // Usar valuesRef.current para evitar stale closures
      const currentValues = valuesRef.current;
      const isRequired = resolveProp(field.config?.required, currentValues);

      if (isRequired) {
        if (value === undefined || value === null || value === '') {
          const fieldName = t(field.accessorKey as string, field.header);

          // Intentar obtener traducciones con prefijo explícito si el t() no lo tiene
          const theField = t('ui-mastercrud.theField', t('theField', ''));
          const isRequiredMsg = t('ui-mastercrud.isRequired', t('isRequired', ''));

          // Si t() no devolvió nada útil (devolvió la llave), aplicamos lógica de idioma
          const isEnglish = t('ui-mastercrud.save', t('save', '')).toLowerCase().includes('save');

          const finalPrefix = theField && !theField.includes('theField')
            ? theField
            : (isEnglish ? 'The field' : 'El campo');

          const finalSuffix = isRequiredMsg && !isRequiredMsg.includes('isRequired')
            ? isRequiredMsg
            : (isEnglish ? 'is required' : 'es requerido');

          return field.config?.errorMessage || `${finalPrefix} ${fieldName} ${finalSuffix}`;
        }
      }

      if (field.config?.validate) {
        return field.config.validate(value, currentValues as Record<string, any>);
      }

      if (value !== undefined && value !== null && value !== '') {
        const strValue = String(value);

        if (field.config?.maxLength !== undefined && strValue.length > field.config.maxLength) {
          return `${t('maxLengthIs', 'La longitud máxima es de')} ${field.config.maxLength} ${t('characters', 'caracteres')}`;
        }

        if (field.config?.minLength !== undefined && strValue.length < field.config.minLength) {
          return `${t('minLengthIs', 'La longitud mínima es de')} ${field.config.minLength} ${t('characters', 'caracteres')}`;
        }

        switch (field.type) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(strValue)) {
              return t('invalidEmail', 'Ingresa un correo electrónico válido');
            }
            break;

          case 'number':
            const numValue = Number(value);
            if (isNaN(numValue)) {
              return t('invalidNumber', 'Ingresa un número válido');
            }
            if (field.config?.min !== undefined && numValue < field.config.min) {
              return `${t('minValueIs', 'El valor mínimo es')} ${field.config.min}`;
            }
            if (field.config?.max !== undefined && numValue > field.config.max) {
              return `${t('maxValueIs', 'El valor máximo es')} ${field.config.max}`;
            }
            break;
        }
      }

      return undefined;
    },
    [t]
  );

  // ===== VALIDAR TODO EL FORMULARIO =====
  const validateForm = useCallback((): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    fields
      .filter((field) => field.config?.visibleInForm !== false)
      .forEach((field) => {
        const value = getValue(field.accessorKey as string);
        const error = validateField(field, value);
        if (error) {
          newErrors[field.accessorKey as string] = error;
          isValid = false;
        }
      });

    setFormState((prev) => ({
      ...prev,
      errors: newErrors,
    }));

    return { isValid, errors: newErrors };
  }, [fields, getValue, validateField]);

  // ===== EFECTO ON MOUNT =====
  useEffect(() => {
    visibleFields.forEach(field => {
      if (field.config?.onMount) {
        const fieldSetter = (val: any) => setValue(field.accessorKey as string, val);
        field.config.onMount(fieldSetter, initialData as Record<string, any>);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== MANEJAR ENVÍO =====
  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isSubmittingRef.current || loading) {
        console.log("[MasterCrudForm] Envío bloqueado: ya se está procesando o el formulario está en loading.");
        return;
      }

      isSubmittingRef.current = true;
      console.log("%c[MasterCrudForm] Iniciando proceso de guardado...", "color: #22c55e; font-weight: bold; padding: 2px; border: 1px solid #22c55e; border-radius: 4px;");

      const { isValid, errors } = validateForm();

      if (isValid) {
        // EXTRAER VALORES ACTUALES DE LA REFERENCIA PARA EVITAR DATOS VIEJOS
        const currentData = { ...valuesRef.current };

        console.log("%c[MasterCrudForm] Envío exitoso. Datos capturados:", "color: #22c55e; font-weight: bold;", currentData);

        setFormState((prev) => ({ ...prev, isSubmitting: true }));
        if (onSubmit) {
          const result = onSubmit(currentData);
          // Si el onSubmit devuelve una promesa, esperamos a que termine para resetear el flag
          if (result && (result as any) instanceof Promise) {
            (result as Promise<any>).finally(() => {
              isSubmittingRef.current = false;
              setFormState(prev => ({ ...prev, isSubmitting: false }));
            });
          } else {
            // Si no es promesa, damos un pequeño margen para evitar dobles clics accidentales
            setTimeout(() => {
              isSubmittingRef.current = false;
              setFormState(prev => ({ ...prev, isSubmitting: false }));
            }, 500);
          }
        }
      } else {
        isSubmittingRef.current = false;
        console.error("[MasterCrudForm] Error de validación. Campos afectados:", errors);
        const errorFields = Object.keys(errors).map(key => {
          const field = fields.find(f => f.accessorKey === key);
          // Traducimos el nombre del campo usando su accessorKey como llave
          return field ? t(field.accessorKey as string, field.header) : key;
        }).join(', ');

        if (showValidationErrorDialog) {
          setErrorAlert({
            show: true,
            title: t('incompleteTitle', 'Información incompleta'),
            message: `${t('incompleteMessage', 'Para guardar, completa los siguientes campos obligatorios')}: ${errorFields}`
          });
        }
      }
    },
    [onSubmit, validateForm, fields, t, showValidationErrorDialog, loading]
  );

  // Alias para mayor claridad en el renderizado
  const handleInternalSubmit = (e?: any) => {
    if (e && e.preventDefault) e.preventDefault();
    handleSubmit();
  };

  // ===== MANEJAR BLUR =====
  const handleBlur = (field: MasterCrudField<T>, value: any) => {
    if (field.config?.onBlur) {
      field.config.onBlur(value, field.accessorKey as string, formState.values as Record<string, any>);
    }

    // Lógica de búsqueda de coincidencias
    const cleanValue = typeof value === 'string' ? value.trim() : '';

    if (
      !isEditing &&
      activeByCompany &&
      field.config?.checkMatchesOnBlur &&
      cleanValue.length >= 3 &&
      cleanValue !== lastSearchedValue.current
    ) {
      lastSearchedValue.current = cleanValue;
      onSearchMatches?.(field, cleanValue);
    }
  };

  // Mensaje de validación bajo el campo — estilo pixel-exact del prototipo de detalle:
  // `flex items-center gap-[3px] text-xs text-[#dc2626]` con icono AlertTriangle de 11px.
  const renderFieldError = (accessorKey: string): React.ReactNode => {
    const msg = formState.errors[accessorKey];
    if (!msg) return null;
    return (
      <p className="field-error flex items-center gap-[3px] mt-1 text-xs text-[#dc2626]">
        <svg className="w-[11px] h-[11px] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
        {msg}
      </p>
    );
  };

  // ===== FUNCIÓN DE RENDERIZADO PARA EL WRAPPER (Evita pérdida de foco al no ser un componente) =====
  const renderFieldWrapper = (
    field: MasterCrudField<T>,
    children: React.ReactNode,
    value: any
  ) => {
    const accessorKey = field.accessorKey as string;
    const isRequired = resolveProp(field.config?.required, formState.values);
    const companyUuid = String(selectedCompany?.uuid || '000');

    // Reglas de activación del Wrapper
    const isOverrideActive = !companyRequired &&
      isEditing &&
      activeByCompany &&
      !!field.config?.allowMultiCompanyOverride &&
      companyUuid !== '000';

    // En contexto de sucursal (edición, compañía no global) todos los campos usan
    // el label custom del wrapper para mantener consistencia visual, independientemente
    // de si tienen allowMultiCompanyOverride o no.
    const isCompanyContext = isEditing && activeByCompany && companyUuid !== '000' && !companyRequired;

    if (!isOverrideActive && !isCompanyContext) return <>{children}{renderFieldError(accessorKey)}</>;

    const globalData = (initialData as any)?._globalValues;

    // Resolución inteligente del valor global para mostrar etiquetas legibles
    const getResolvedGlobalValue = (): React.ReactNode => {
      // 1. Prioridad: Función personalizada del desarrollador
      if (field.config?.getGlobalValue) return field.config.getGlobalValue(initialData);

      const rawValue = globalData?.[accessorKey];
      if (rawValue === undefined || rawValue === null) return t('undefined', 'Sin definir');

      // 2. Resolución Automática para Lookup (NUEVO)
      if (field.type === 'lookup' && field.lookupConfig) {
        return (
          <GlobalLookupResolver
            id={rawValue}
            lookupConfig={field.lookupConfig}
            t={t}
          />
        );
      }

      // 3. Resolución para Select
      if (field.type === 'select' && field.options) {
        const option = field.options.find(opt => String(opt.value) === String(rawValue));
        return option ? option.label : String(rawValue);
      }

      // 4. Resolución para Boolean
      if (field.type === 'boolean') {
        return !!rawValue ? t('yes', 'Sí') : t('no', 'No');
      }

      // 5. Default: String
      return String(rawValue);
    };

    const globalValueText = getResolvedGlobalValue();

    // Comparación para mostrar botón de revertir
    const hasOverride = globalData &&
      globalData[accessorKey] !== undefined &&
      String(value) !== String(globalData[accessorKey]);

    return (
      <div className="flex flex-col gap-2 w-full mb-2">
        <div className="flex items-center w-full font-bold text-sm leading-5 text-content-primary dark:text-dark-content-primary">
          <span>
            {t(field.accessorKey as string, field.header)}
            {isRequired && <span className="ml-1 text-error-content">*</span>}
          </span>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center gap-2 w-full">
            <div className="flex-1 min-w-0">
              {children}
            </div>
            {hasOverride && (
              <div className="shrink-0">
                <Button
                  type="outline"
                  iconOnly
                  leftIcon={<RevertIcon className="w-[12px] h-[12px]" />}
                  onClick={() => handleRevertSingleField(field)}
                  ariaLabel={t('revertToGlobal', 'Revertir a valor global')}
                  title={t('revertToGlobal', 'Revertir a valor global')}
                  className="
                    !h-[38px]
                    !w-[38px]
                    !bg-[#eff8ff]
                    !border-[#93d1fd]
                    !text-[#1355b7]
                    hover:!bg-[#dbeefe]
                    dark:!bg-primary-900/30
                    dark:!border-primary-800
                    dark:!text-primary-300
                    transition-all
                  "
                />
              </div>
            )}
          </div>
          {/* Mensaje de validación pegado al campo (mt-1 = 4px, como el prototipo) */}
          {renderFieldError(accessorKey)}
        </div>

        {isOverrideActive && showGlobalValues && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <GlobeAltIcon className="w-3.5 h-3.5 text-[#1355b7] dark:text-primary-400 shrink-0" />
            <p className="text-[12px] font-medium leading-4 text-[#1355b7] dark:text-primary-400 italic">
              {t('globalValue', 'Valor maestro')}: <span className="font-semibold">{globalValueText}</span>
            </p>
          </div>
        )}
      </div>
    );
  };

  // ===== RENDERIZAR CAMPO POR DEFECTO =====
  function renderDefaultField(field: MasterCrudField<T>): React.ReactNode {
    const accessorKey = field.accessorKey as string;
    const value = getValue(accessorKey);
    const error = formState.errors[accessorKey];

    // Lógica de deshabilitado base
    const isConfigDisabled = resolveProp(field.config?.disabled, formState.values);

    // Lógica de sobreescritura multicompañía
    const companyUuid = String(selectedCompany?.uuid || '000');
    const isOverrideActive = isEditing &&
      activeByCompany &&
      !!field.config?.allowMultiCompanyOverride &&
      companyUuid !== '000';

    const isRestrictedByCompany = isEditing &&
      activeByCompany &&
      companyUuid !== '000' &&
      !companyRequired &&
      !field.config?.allowMultiCompanyOverride;

    const isOverrideForbidden = isOverrideActive && !canOverrideMultiCompany;

    const isDisabled = loading ||
      isReadOnly ||
      isConfigDisabled ||
      (isEditing && !!field.config?.disabledOnEdit) ||
      isRestrictedByCompany ||
      isOverrideForbidden;

    const isRequired = resolveProp(field.config?.required, formState.values);

    const fieldContent = (() => {
      if (field.renderInput) {
        return field.renderInput(
          value,
          (newValue) => setValue(accessorKey, newValue),
          field,
          formState.values as Record<string, any>
        );
      }

      // El label lo gestiona siempre renderFieldWrapper cuando estamos en contexto de compañía
      // o cuando isOverrideActive es true. En ambos casos ocultamos el label del componente hijo.
      const isCompanyContext = isEditing && activeByCompany && companyUuid !== '000' && !companyRequired;
      // En contexto de wrapper (override/compañía) el asterisco de requerido lo pinta la
      // etiqueta del propio wrapper; el control interno NO debe repetirlo (evita doble "*").
      const inWrapper = isOverrideActive || isCompanyContext;
      const commonProps = {
        label: !inWrapper ? t(field.accessorKey as string, field.header) : undefined,
        error: !!error,
        disabled: isDisabled,
        required: inWrapper ? false : isRequired,
      };

      switch (field.type) {
        case 'text':
        case 'email':
          return (
            <Input
              {...commonProps}
              label={!isOverrideActive ? (commonProps.label as string) : undefined}
              type={field.type as 'text' | 'email'}
              value={value ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(accessorKey, e.target.value)}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleBlur(field, e.target.value)}
              placeholder={field.config?.placeholder}
              error={!!error}              maxLength={field.config?.maxLength}
              {...({} as any)}
            />
          );

        case 'number':
          return (
            <Input
              {...commonProps}
              label={!isOverrideActive ? (commonProps.label as string) : undefined}
              type="number"
              value={value ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(accessorKey, e.target.value ? Number(e.target.value) : '')}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleBlur(field, e.target.value ? Number(e.target.value) : '')}
              placeholder={field.config?.placeholder}
              error={!!error}              min={field.config?.min}
              max={field.config?.max}
              step={field.config?.step}
              {...({} as any)}
            />
          );

        case 'date':
          return (
            <Input
              {...commonProps}
              label={!isOverrideActive ? (commonProps.label as string) : undefined}
              type="date"
              value={value ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(accessorKey, e.target.value)}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleBlur(field, e.target.value)}
              placeholder={field.config?.placeholder}
              error={!!error}            />
          );

        case 'boolean': {
          if (isOverrideActive || isCompanyContext) {
            // En contexto override o de sucursal: el label lo gestiona renderFieldWrapper
            return (
              <div className="flex items-center h-10">
                <Switch
                  checked={!!value}
                  onCheckedChange={(checked) => setValue(accessorKey, checked)}
                  disabled={isDisabled}
                />
              </div>
            );
          }
          return (
            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-bold leading-5 text-content-primary dark:text-dark-content-primary">
                {t(field.accessorKey as string, field.header)}
                {isRequired && <span className="ml-1 text-error-content">*</span>}
              </span>

              <Switch
                checked={!!value}
                onCheckedChange={(checked) => setValue(accessorKey, checked)}
                disabled={isDisabled}
              />
            </div>
          );
        }

        case 'select': {
          return (
            <Select
              {...commonProps}
              label={!isOverrideActive ? (commonProps.label as string) : undefined}
              options={
                field.options?.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                  icon: opt.icon,
                  disabled: opt.disabled,
                })) ?? []
              }
              value={value}
              onChange={(newValue) => setValue(accessorKey, newValue)}
              onBlur={() => handleBlur(field, value)}
              placeholder={field.config?.placeholder || `${t('select', 'Seleccionar')}...`}
              description={field.config?.helperText}              selectSize="sm"
            />
          );
        }

        case 'lookup':
          if (!field.lookupConfig) return null;
          return (
            <LookupField
              {...commonProps}
              label={!isOverrideActive ? (commonProps.label as string) : undefined}
              className={isOverrideActive ? '!gap-0' : ''}
              entity={field.lookupConfig.entity}
              fetcher={field.lookupConfig.fetcher}
              displayFields={field.lookupConfig.displayFields}
              value={value ? String(value) : null}
              onChange={(record: any) => {
                if (!record) {
                  setValue(accessorKey, null);
                  handleBlur(field, null);
                  return;
                }
                const valField = field.lookupConfig?.valueField || 'Id';
                let newValue = record[valField];
                if (newValue === undefined && valField === 'Id') {
                  if (record.id !== undefined) newValue = record.id;
                }
                setValue(accessorKey, newValue);
                handleBlur(field, newValue);
              }}
              placeholder={field.config?.placeholder || `${t('search', 'Buscar')}...`}
            />
          );

        default:
          return (
            <Input
              {...commonProps}
              label={!isOverrideActive ? (commonProps.label as string) : undefined}
              type="text"
              value={value ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(accessorKey, e.target.value)}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleBlur(field, e.target.value)}
              placeholder={field.config?.placeholder}
              error={!!error}            />
          );
      }
    })();

    return renderFieldWrapper(field, fieldContent, value);
  }

  // ===== HELPER PARA RENDERIZADO PERSONALIZADO O POR DEFECTO =====
  const renderCustomField = useCallback((
    accessorKey: string,
    inputRenderer?: (value: any, onChange: (val: any) => void) => React.ReactNode
  ) => {
    const field = fields.find(f => f.accessorKey === accessorKey);
    if (!field) return null;

    // Si no hay inputRenderer, usamos el renderizado por defecto del sistema
    if (!inputRenderer) {
      return renderDefaultField(field);
    }

    const value = getValue(accessorKey);
    const onChange = (newValue: any) => setValue(accessorKey, newValue);

    return renderFieldWrapper(field, inputRenderer(value, onChange), value);
  }, [fields, getValue, setValue, showGlobalValues]); // showGlobalValues añadido para reactividad en renderForm

  // ===== FILTRAR CAMPOS VISIBLES =====
  const visibleFields = fields.filter((field) => {
    if (isEditing) {
      if (field.config?.showInEdit !== undefined) return field.config.showInEdit;
      if (field.config?.visibleInForm !== undefined) return field.config.visibleInForm;
      return true;
    }
    if (field.config?.showInCreate !== undefined) return field.config.showInCreate;
    if (field.config?.visibleInForm !== undefined) return field.config.visibleInForm;
    return true;
  });

  // ===== CLASES DEL CONTENEDOR =====
  const containerClasses = `
    flex
    flex-col
    bg-white
    dark:bg-dark-bg-primary
    max-h-full
    w-full
    ${className}
  `
    .replace(/\s+/g, ' ')
    .trim();

  // ===== CLASES DEL GRID =====
  const gridClasses = `
    grid
    grid-cols-1
    ${columns >= 2 ? 'md:grid-cols-2' : ''}
    ${columns >= 3 ? 'lg:grid-cols-3' : ''}
    ${columns >= 4 ? 'xl:grid-cols-4' : ''}
    gap-x-10
    gap-y-3
  `
    .replace(/\s+/g, ' ')
    .trim();

  // ===== RENDERIZADO DE SKELETON (MÁSCARA DE FORMULARIO) =====
  const renderSkeleton = () => (
    <div className={gridClasses}>
      {visibleFields.map((field, idx) => {
        const colSpan = field.config?.colSpan;
        return (
          <div
            key={`skeleton-${idx}`}
            style={{
              gridColumn: colSpan ? `span ${colSpan} / span ${colSpan}` : undefined
            }}
            className="flex flex-col gap-2"
          >
            {/* Label Skeleton */}
            <div className="h-4 w-24 bg-border-primary/50 dark:bg-dark-border-primary/30 rounded animate-pulse" />
            {/* Input Skeleton */}
            <div className="h-10 w-full bg-border-primary/30 dark:bg-dark-border-primary/20 rounded-lg animate-pulse" />
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={containerClasses} style={{ maxWidth: width }}>
      {/* Alerta de Error de Validación (usa el portal del propio AlertDialog: una sola capa,
          sin backdrop duplicado que bloquee la interacción). */}
      <AlertDialog
        isOpen={errorAlert.show}
        type="warning"
        hideCancel
        title={errorAlert.title || t('incompleteTitle', 'Información incompleta')}
        description={errorAlert.message}
        onCancel={() => setErrorAlert({ show: false, message: '' })}
        onConfirm={() => setErrorAlert({ show: false, message: '' })}
        confirmText={t('accept', 'Entendido')}
      />

      {/* Advertencia DEV: renderForm sin MasterCrudDynamicEntities */}
      {process.env.NODE_ENV === 'development' && devDynamicEntitiesWarning && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4">
          <AlertDialog
            title="Error de implementación de maestro"
            onConfirm={() => setDevDynamicEntitiesWarning(false)}
            confirmText="Entendido"
          >
            <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
              {`A pesar de que el maestro se encuentra asociado al feature "${entityName || dynamicFeatureCode}" no tiene implementado el componente de entidades dinámicas. Para no ver este mensaje de error, proceda a incluir el componente de entidades dinámicas en el RenderForm del maestro.`}
            </p>
          </AlertDialog>
        </div>
      )}

      {/* Advertencia DEV: renderForm sin MasterCrudFileManager cuando se requiere gestión de archivos */}
      {process.env.NODE_ENV === 'development' && devFileManagerWarning && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 p-4">
          <AlertDialog
            title="Error de implementación de maestro"
            onConfirm={() => setDevFileManagerWarning(false)}
            confirmText="Entendido"
          >
            <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
              {`El maestro tiene habilitada la gestión de archivos (feature "${featCodeFileManager}") pero no incluye el componente MasterCrudFileManager en su renderForm. Para no ver este mensaje de error, incluya <MasterCrudFileManager /> dentro del renderForm del maestro.`}
            </p>
          </AlertDialog>
        </div>
      )}

      {/* Header con botón X */}
      {!hideHeaderFooter && (
        <div className="flex-none p-5 pb-2 flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1 min-w-0">
            <h2 className="text-[16px] font-medium text-primary-600 dark:text-primary-500 truncate">
              {isEditing ? `${t('edit', 'Editar')} ${entityName || t('record', 'Registro')}` : `${t('create', 'Nuevo')} ${entityName || t('record', 'Registro')}`}
            </h2>
            {/* Selector de Compañía (solo en Modal/Sidebar) */}
            {activeByCompany && companies.length > 0 && (
              <div className={loading && isEditing ? 'animate-pulse opacity-50' : ''}>
                <CompanySelector
                  companies={companies}
                  selectedCompany={selectedCompany}
                  onCompanyChange={(c) => onCompanyChange?.(c, (initialData as any)?.id)}
                  readOnly={companyRequired}
                  t={t}
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 h-6">
            {/* Acciones Automáticas y Personalizadas (solo en Modal/Sidebar) */}
            {headerDropdownItems.length > 0 && (
              <Dropdown
                menuClassName="w-56"
                renderTrigger={() => (
                  <Button
                    type="outline"
                    size="xs"
                    iconOnly
                    leftIcon={<Bars3Icon className="w-4 h-4" />}
                    ariaLabel={t('actions', 'Acciones')}
                    title={t('actions', 'Acciones')}
                    disabled={loading}
                    className="flex" // Forzar comportamiento flex para evitar baseline shifts
                  />
                )}
                items={headerDropdownItems}
              />
            )}
            <Button
              type="outline"
              size="xs"
              iconOnly
              leftIcon={<XMarkIcon className="w-4 h-4" />}
              onClick={onCancel}
              ariaLabel={t('close', 'Cerrar')}
              className="!bg-white dark:!bg-dark-bg-primary flex"
            />
          </div>
        </div>
      )}

      {/* Formulario */}
      <form id={formId} ref={formElementRef} noValidate onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        {/* TABS DE NAVEGACIÓN (pestañas de campos formTabs y/o entidades dinámicas / archivos) */}
        {(hasFieldTabs || (dynamicFeatureCode && dynamicFeature && dynamicFeature.featureEntities?.length && !effectiveHideDynamicTabs) || hasFileManagement) ? (
          <div className="flex-shrink-0 bg-transparent">
            <div ref={tabsRowRef} className="relative flex items-stretch gap-1 px-3 pt-2">
              {hasFieldTabs ? (
                // Pestañas de campos (formTabs) — cada una con su badge de faltantes
                fieldTabs.map((ft) => {
                  const errCount = Object.keys(formState.errors).filter((k) => getFieldTabKey(k) === ft.key).length;
                  const active = activeGlobalTab === ft.key;
                  return (
                    <button
                      key={ft.key}
                      type="button"
                      data-tabkey={ft.key}
                      onClick={() => setActiveGlobalTab(ft.key)}
                      className={`tab-btn relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer rounded-t-lg bg-[#f8f9fc] dark:bg-dark-bg-secondary transition-colors focus:outline-none ${active
                        ? 'text-[#0c6ade]'
                        : 'text-content-tertiary dark:text-dark-content-tertiary hover:text-content-primary dark:hover:text-dark-content-primary hover:bg-[#eef0f3] dark:hover:bg-white/[0.06]'
                        }`}
                    >
                      {ft.icon && <span className="inline-flex items-center justify-center w-3.5 h-3.5">{ft.icon}</span>}
                      {ft.label}
                      {errCount > 0 && (
                        <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 bg-[#dc2626] text-white rounded-full text-[9px] font-bold">{errCount}</span>
                      )}
                    </button>
                  );
                })
              ) : (
                <button
                  type="button"
                  data-tabkey="main"
                  onClick={() => setActiveGlobalTab('main')}
                  className={`tab-btn relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer rounded-t-lg bg-[#f8f9fc] dark:bg-dark-bg-secondary transition-colors focus:outline-none ${activeGlobalTab === 'main'
                    ? 'text-[#0c6ade]'
                    : 'text-content-tertiary dark:text-dark-content-tertiary hover:text-content-primary dark:hover:text-dark-content-primary hover:bg-[#eef0f3] dark:hover:bg-white/[0.06]'
                    }`}
                >
                  {t('mainInfo', 'Información Principal')}
                  {Object.keys(formState.errors).length > 0 && (
                    <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 bg-[#dc2626] text-white rounded-full text-[9px] font-bold">{Object.keys(formState.errors).length}</span>
                  )}
                </button>
              )}

              {hasStandardDynamicEntities && (
                <button
                  type="button"
                  data-tabkey="dynamic_entities"
                  onClick={() => setActiveGlobalTab('dynamic_entities')}
                  className={`tab-btn relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer rounded-t-lg bg-[#f8f9fc] dark:bg-dark-bg-secondary transition-colors focus:outline-none ${activeGlobalTab === 'dynamic_entities'
                    ? 'text-[#0c6ade]'
                    : 'text-content-tertiary dark:text-dark-content-tertiary hover:text-content-primary dark:hover:text-dark-content-primary hover:bg-[#eef0f3] dark:hover:bg-white/[0.06]'
                    }`}
                >
                  {t('dynamicEntities', 'Datos Adicionales')}
                </button>
              )}

              {hasPlansAndCriteriaEntities && (
                <button
                  type="button"
                  data-tabkey="plans_and_criteria"
                  onClick={() => setActiveGlobalTab('plans_and_criteria')}
                  className={`tab-btn relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer rounded-t-lg bg-[#f8f9fc] dark:bg-dark-bg-secondary transition-colors focus:outline-none ${activeGlobalTab === 'plans_and_criteria'
                    ? 'text-[#0c6ade]'
                    : 'text-content-tertiary dark:text-dark-content-tertiary hover:text-content-primary dark:hover:text-dark-content-primary hover:bg-[#eef0f3] dark:hover:bg-white/[0.06]'
                    }`}
                >
                  {t('plansAndCriteria', 'Planes y Criterios')}
                </button>
              )}

              {hasFileManagement && (
                <button
                  type="button"
                  data-tabkey="file_management"
                  onClick={() => setActiveGlobalTab('file_management')}
                  className={`tab-btn relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer rounded-t-lg bg-[#f8f9fc] dark:bg-dark-bg-secondary transition-colors focus:outline-none ${activeGlobalTab === 'file_management'
                    ? 'text-[#0c6ade]'
                    : 'text-content-tertiary dark:text-dark-content-tertiary hover:text-content-primary dark:hover:text-dark-content-primary hover:bg-[#eef0f3] dark:hover:bg-white/[0.06]'
                    }`}
                >
                  {t('fileManagement', 'Gestión de Archivos')}
                </button>
              )}
              {/* Indicador deslizante con rebote (grosor constante 2.5px) */}
              <span
                ref={tabIndicatorRef}
                aria-hidden="true"
                className="absolute bottom-0 h-[2.5px] rounded-[3px] bg-[#0e79fd] pointer-events-none"
                style={{ left: 0, width: 0, transition: 'left .5s cubic-bezier(.34,1.56,.64,1), width .42s cubic-bezier(.22,1,.36,1)' }}
              />
            </div>
          </div>
        ) : null}

        <div className={`flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-border-primary dark:scrollbar-thumb-dark-border-primary ${hideHeaderFooter ? 'p-6 md:p-8' : 'p-5 pt-2'}`}>
          <div className="flex flex-col gap-6">
            <div className={(hasFieldTabs ? fieldTabs.some(ft => ft.key === activeGlobalTab) : (effectiveHideDynamicTabs || !dynamicFeatureCode || !dynamicFeature || activeGlobalTab === 'main')) ? 'block animate-in fade-in duration-300' : 'hidden'}>
              {loading && isEditing && !initialData?.[idField as any] ? (
                renderSkeleton()
              ) : renderContent ? (
                <FileGalleryRegistrationContext.Provider value={fileGalleryRegistrationContextValue}>
                <DynamicEntitiesRegistrationContext.Provider value={registrationContextValue}>
                  {renderContent({
                    fields,
                    entityName,
                    initialData,
                    isEditing,
                    loading,
                    onSubmit: (e?: any) => {
                      // Si el componente hijo nos pasa un objeto de datos directamente (no un evento DOM),
                      // lo relayamos sin pasar por el handleSubmit interno que solo tiene campos overridables.
                      if (e !== undefined && e !== null && !(e instanceof Event) && typeof e === 'object' && !('nativeEvent' in e) && typeof e.preventDefault !== 'function') {
                        onSubmit?.(e);
                        return;
                      }
                      if (e && typeof e.preventDefault === 'function') e.preventDefault();
                      handleSubmit();
                    },
                    onCancel,
                    saveButtonText,
                    cancelButtonText,
                    columns,
                    width,
                    className,
                    actions,
                    hideHeaderFooter,
                    fullWidthButton,
                    isReadOnly,
                    id: formId,
                    selectedCompany,
                    t,
                    activeByCompany,
                    showGlobalValues,
                    onSearchMatches,
                    canOverrideMultiCompany,
                    companies,
                    onCompanyChange,
                    onShowGlobalValuesChange,
                    onUseGlobalData,
                    onCreateNew,
                    onLinkCompanies,
                    registerDynamicRef,
                    values: formState.values,
                    setValue: (name: string, val: any) => setValue(name, val),
                    errors: formState.errors,
                    hideDynamicTabs: effectiveHideDynamicTabs,
                    dynamicFeature,
                    dynamicFeatureCode,
                    renderField: renderCustomField
                  })}
                </DynamicEntitiesRegistrationContext.Provider>
                </FileGalleryRegistrationContext.Provider>
              ) : (
                <div className={gridClasses}>
                  {visibleFields
                    .filter((field) => !hasFieldTabs || getFieldTabKey(field.accessorKey as string) === activeGlobalTab)
                    .map((field) => {
                      const colSpan = field.config?.colSpan;
                      return (
                        <div
                          key={field.accessorKey as string}
                          style={{
                            gridColumn: colSpan ? `span ${colSpan} / span ${colSpan}` : undefined
                          }}
                        >
                          {renderDefaultField(field)}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Contenedor Unificado de Entidades Dinámicas con soporte interno de Inlines/Tabs */}
            {dynamicFeatureCode && dynamicFeature && dynamicFeature.featureEntities?.length && !effectiveHideDynamicTabs ? (
              <>
                <div className={activeGlobalTab === 'dynamic_entities' ? 'block animate-in fade-in duration-300 space-y-6' : 'hidden'}>
                  <MasterCrudDynamicEntities
                    dynamicFeature={dynamicFeature}
                    dynamicFeatureCode={dynamicFeatureCode}
                    recordId={(initialData as any)?.id}
                    registerDynamicRef={(entityCode, el) => { dynamicRefs.current[entityCode] = el; }}
                    showOnlyPlansAndCriteria={false}
                  />
                </div>
                <div className={activeGlobalTab === 'plans_and_criteria' ? 'block animate-in fade-in duration-300 space-y-6' : 'hidden'}>
                  <MasterCrudDynamicEntities
                    dynamicFeature={dynamicFeature}
                    dynamicFeatureCode={dynamicFeatureCode}
                    recordId={(initialData as any)?.id}
                    registerDynamicRef={(entityCode, el) => { dynamicRefs.current[entityCode] = el; }}
                    showOnlyPlansAndCriteria={true}
                  />
                </div>
              </>
            ) : null}

            {/* Gestión de Archivos tab content */}
            {hasFileManagement && (
              <div className={activeGlobalTab === 'file_management' ? 'block animate-in fade-in duration-300' : 'hidden'}>
                <FileGalleryRegistrationContext.Provider value={fileGalleryRegistrationContextValue}>
                  <MasterCrudFileManager
                    entityId={(initialData as any)?.id}
                    entityName={entityName || ''}
                    config={fileManagerConfig!}
                  />
                </FileGalleryRegistrationContext.Provider>
              </div>
            )}
          </div>
        </div>

        {/* Botones de acción - Footer Independiente (Asegura clicabilidad absoluta) */}
        {!hideHeaderFooter && (
          <div
            className="flex-none p-5 pt-3 pb-5 bg-white dark:bg-dark-bg-primary border-t border-border-primary/50 dark:border-dark-border-primary/30 relative z-[200] pointer-events-auto shadow-sm"
            style={{ position: 'sticky', bottom: 0, marginTop: 'auto' }}
          >
            {fullWidthButton ? (
              <div className="flex flex-col gap-3">
                <Button
                  type="default"
                  size="l"
                  onClick={(e: any) => {
                    console.log("%c[CLICK-FORCE] Disparando guardado...", "background: #22c55e; color: #fff; padding: 2px 5px; border-radius: 4px;");
                    if (e && e.preventDefault) e.preventDefault();
                    if (e && e.stopPropagation) e.stopPropagation();
                    handleSubmit();
                  }}
                  disabled={loading}
                  fullWidth
                  className="font-bold uppercase tracking-wide"
                >
                  {loading ? t('loading', 'Cargando...') : finalSaveText}
                </Button>
              </div>
            ) : (
              <div className="flex justify-end items-center gap-4">
                <Button
                  type="outline"
                  size="l"
                  onClick={onCancel}
                  disabled={loading}
                  className="font-medium px-6 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  {finalCancelText}
                </Button>
                <Button
                  type="default"
                  size="l"
                  onClick={(e: any) => {
                    console.log("%c[CLICK-FORCE] Disparando guardado...", "background: #22c55e; color: #fff; padding: 2px 5px; border-radius: 4px;");
                    if (e && e.preventDefault) e.preventDefault();
                    if (e && e.stopPropagation) e.stopPropagation();
                    handleSubmit();
                  }}
                  disabled={loading}
                  className="font-bold px-10 uppercase tracking-wide"
                >
                  {loading && !isEditing ? t('saving', 'Guardando...') : loading ? t('loading', 'Cargando...') : finalSaveText}
                </Button>
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );

});

MasterCrudForm.displayName = 'MasterCrudForm';
